import { BaseApiService } from '@/shared/api/services/base.service';
import { buildUrl, type FilterValue } from '@/shared/lib/query-params';
import { conceptsService } from '@/shared/api/services/concepts.service';
import type { Concept } from '@/types/concept';
import type {
  HistoryParams,
  HistoryResponse,
  RenderHistoryResponse,
  JobHistoryResponse,
} from '../model/history.types';

/**
 * Unified history service.
 *
 * Uses single /v1/history endpoint with filter[type] parameter:
 * - filter[type]=jobs (default) — returns renders + albums
 * - filter[type]=concepts — returns concepts with previews
 */
class HistoryService extends BaseApiService {
  async getConceptsHistory(params?: HistoryParams): Promise<HistoryResponse> {
    const url = buildUrl('/v1/history', {
      filter: {
        type: 'concepts',
        ...params?.filter,
      } as Record<string, FilterValue>,
      sort: params?.sort,
      page: params?.page,
    });
    return this.httpGet<HistoryResponse>(url);
  }

  async getJobsHistory(params?: HistoryParams): Promise<JobHistoryResponse> {
    const url = buildUrl('/v1/history', {
      filter: params?.filter as Record<string, FilterValue> | undefined,
      sort: params?.sort,
      page: params?.page,
    });
    return this.httpGet<JobHistoryResponse>(url);
  }

  /** @deprecated Use getJobsHistory instead */
  async getRenderHistory(
    params?: HistoryParams
  ): Promise<RenderHistoryResponse> {
    return this.getJobsHistory(params) as Promise<RenderHistoryResponse>;
  }

  /** @deprecated Use getConceptsHistory instead */
  async getHistory(params?: HistoryParams): Promise<HistoryResponse> {
    return this.getConceptsHistory(params);
  }

  async toggleFavorite(
    projectId: string,
    conceptId: string,
    newIsFavorite: boolean
  ): Promise<Concept> {
    return conceptsService.updateConcept(projectId, conceptId, {
      is_favorite: newIsFavorite,
    });
  }

  async deleteConcept(projectId: string, conceptId: string): Promise<void> {
    return conceptsService.deleteConcept(projectId, conceptId);
  }

  async deleteRender(renderId: string): Promise<void> {
    await this.httpDelete(`/v1/renders/${renderId}`);
  }
}

export const historyService = new HistoryService();
